import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { DYNAMIC_LISTS_PAGE_ROUTES } from '@galaxy/crm/integrations/dynamic-lists';
import { PAGE_ROUTES } from '@galaxy/crm/static';

export class CachedRouteReuseStrategy implements RouteReuseStrategy {
  private routesToCache: string[] = [
    `crm/${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.EDIT}`,
    `crm/${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`,
    `crm/${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.EDIT}`,
    `crm/${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`,
    DYNAMIC_LISTS_PAGE_ROUTES.EDIT,
  ];
  // A map to cache components, using the route path as the key and the component instance as the value.
  private cache: Map<string, DetachedRouteHandle | null> = new Map();
  // Routes need to specify they want to be reused with shouldCacheRoute
  private shouldCacheRouteValue(route: ActivatedRouteSnapshot): boolean {
    return this.routesToCache.includes(this.cacheKey(route));
  }
  private cacheKey(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .map((el: ActivatedRouteSnapshot) => (el.routeConfig ? el.routeConfig.path : ''))
      .filter((str) => str && str.length > 0)
      .join('/');
  }

  // 1. Checks if the route configuration for the target route (future) matches the route configuration for the current route (curr).
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  // 2. Determines if a cached instance of the component for the target route exists.
  // If it returns true, Angular calls the `retrieve` method to restore the cached component.
  // If it returns false, Angular will create and initialize a new component instance.
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this.shouldCacheRouteValue(route) && !!this.cache.get(this.cacheKey(route));
  }

  // 3. Retrieves the cached component for the given route.
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this.cache.get(this.cacheKey(route)) || null;
  }

  // 4. Determines if the component we are navigating away from should be cached.
  // If it returns true, Angular calls the `store` method to save the component for future use.
  // If it returns false, Angular destroys the component.
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.shouldCacheRouteValue(route);
  }

  // Stores the component for the given route in the cache
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (this.shouldCacheRouteValue(route) && this.cacheKey(route) && handle) {
      this.cache.set(this.cacheKey(route), handle);
    }
  }
}
